"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import Link from "next/link";
import { FormattedMessage, useIntl } from "react-intl";
import { FaArrowRight, FaCheck, FaChevronUp, FaRegFileLines } from "react-icons/fa6";
import type { Locale } from "@/lang";
import { trackCtaClick, trackEvent } from "@/app/providers/ObservabilityProvider";
import { appTargetOfUrl, useAppLink } from "@/lib/analytics/app-links";
import { homePath } from "@/lib/locale-routing";
import { SPOT_TONES, SpotIcon } from "../Spot/SpotIcon";
import { StoryLottie, phoneStageClass } from "../Story/StoryLottie";
import { GUIDE_FEATURE_GROUPS, GUIDE_STEPS, type GuideArticleLinks, type GuideStep } from "./guide-data";

const FEATURES_ID = "fonctionnalites";
/** Sections the table of contents follows, in page order. */
const SECTION_IDS = [...GUIDE_STEPS.map((s) => s.id), FEATURES_ID];
/** Anchored sections land under the floating navbar (and the promo banner when shown). */
const SCROLL_MARGIN = "scroll-mt-[calc(var(--promo-h,0px)+7.5rem)]";

/**
 * Rich-text tags allowed in guide messages: `<b>` marks an interface label. A message may
 * hold several labels, each a distinct text, so the text itself serves as the list key.
 */
const rich = {
  b: (chunks: ReactNode) => (
    <strong key={String(chunks)} className="font-semibold text-title-blue">
      {chunks}
    </strong>
  ),
};

/**
 * Tracks one element's visibility.
 *
 * @param rootMargin - IntersectionObserver root margin.
 * @param threshold - Visible ratio that counts as "in view".
 * @returns A ref to attach and whether the element is in view.
 */
function useInView<T extends Element>(rootMargin: string, threshold = 0) {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { rootMargin, threshold });
    io.observe(el);
    return () => io.disconnect();
  }, [rootMargin, threshold]);
  return [ref, inView] as const;
}

/**
 * The section being read, for the table of contents: the last one whose top has passed a
 * reading line at 40 % of the viewport. A single line (not a band) means two sections can
 * never both claim it, so the contents always name the step whose title was just read.
 * Measured at most once per frame while scrolling. Null above the first step.
 *
 * @param ids - Section ids, in page order.
 * @returns The active id.
 */
function useActiveSection(ids: string[]): string | null {
  const [active, setActive] = useState<string | null>(null);
  useEffect(() => {
    let frame = 0;
    const measure = () => {
      frame = 0;
      const line = window.innerHeight * 0.4;
      let current: string | null = null;
      for (const id of ids) {
        const top = document.getElementById(id)?.getBoundingClientRect().top;
        if (top !== undefined && top <= line) current = id;
      }
      setActive(current);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };
    schedule();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, [ids]);
  return active;
}

/**
 * Index of the step being read: -1 above the first step, the number of steps once the
 * reader has reached the feature map (every step then counts as read).
 */
function stepIndex(active: string | null): number {
  return active === FEATURES_ID ? GUIDE_STEPS.length : GUIDE_STEPS.findIndex((s) => s.id === active);
}

/** French puts a non-breaking space before a colon, English does not. */
const colon = (locale: Locale) => (locale === "fr" ? " : " : ": ");

type GuideClientProps = {
  locale: Locale;
  /** Published articles the guide may link to, resolved on the server. */
  links: GuideArticleLinks;
};

/**
 * The getting-started guide, laid out like a help-centre article: a short header, then
 * seven steps separated by whitespace only (title, lead, numbered how-to, an optional tip
 * and the detailed articles, beside the step's demo), a feature map and a help block.
 * Each step keeps its own colour (number, demo ground, contents marker) so the reader
 * always knows where they are without extra labels. Desktop keeps the contents as a
 * sticky progress rail on the left; phones and tablets get the same contents from a
 * pill pinned to the bottom of the screen.
 */
export function GuideClient({ locale, links }: GuideClientProps) {
  const intl = useIntl();
  const { href: appUrl, target: appTarget } = useAppLink("guide");
  const { href: helpAppUrl, target: helpAppTarget } = useAppLink("guide-help");
  const active = useActiveSection(SECTION_IDS);
  const [helpRef, helpInView] = useInView<HTMLElement>("0px");
  const resourcesHref = locale === "en" ? "/resources/en" : "/resources";

  const stepTitle = (s: GuideStep) => intl.formatMessage({ id: `guide.step.${s.id}.title` });

  return (
    <div className="bg-hero-wash">
      <div className="mx-auto max-w-6xl px-4 pb-24 pt-32 sm:px-6 sm:pt-36 lg:px-10 lg:pt-40 xl:max-w-7xl 2xl:max-w-[1440px] min-[1920px]:max-w-[1680px]">
        <div className="lg:grid lg:grid-cols-[200px_minmax(0,1fr)] lg:gap-12 xl:grid-cols-[232px_minmax(0,1fr)] xl:gap-16 2xl:grid-cols-[248px_minmax(0,1fr)] 2xl:gap-24">
          {/* Contents: a sticky progress rail beside the whole guide, header included. */}
          <aside className="hidden lg:block">
            <nav
              aria-label={intl.formatMessage({ id: "guide.toc.label" })}
              className="sticky top-[calc(var(--promo-h,0px)+7.5rem)] mt-3"
            >
              <TocContents active={active} stepTitle={stepTitle} resourcesHref={resourcesHref} />
            </nav>
          </aside>

          <div className="min-w-0">
            <header className="max-w-3xl pb-4 sm:pb-6">
              <h1 className="text-balance text-4xl font-bold leading-[1.06] tracking-[-0.025em] text-title-blue sm:text-5xl">
                <FormattedMessage id="guide.title" />
                <span className="text-brand">.</span>
              </h1>
              <p className="mt-5 max-w-2xl text-lg leading-relaxed text-text-main">
                <FormattedMessage id="guide.lead" />
              </p>
              <Link
                href={appUrl}
                onClick={() => {
                  trackEvent("guide-signup-button", {}, { criticalOnNav: true });
                  trackCtaClick("guide", appTarget);
                }}
                className="group/cta mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-blue-main px-6 py-3 text-base font-semibold text-white transition-colors hover:bg-blue-main/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-main"
              >
                <FormattedMessage id="guide.cta.signup" />
                <FaArrowRight
                  aria-hidden="true"
                  className="text-xs transition-transform group-hover/cta:translate-x-1 motion-reduce:transition-none"
                />
              </Link>
            </header>

            {GUIDE_STEPS.map((step, k) => (
              <StepSection key={step.id} step={step} n={k + 1} locale={locale} links={links} stepTitle={stepTitle} />
            ))}

            <FeatureMap links={links} />

            <section
              ref={helpRef}
              aria-labelledby="guide-help-title"
              className="mt-20 flex flex-col gap-6 rounded-[28px] bg-blue-ulight p-6 sm:flex-row sm:items-center sm:justify-between sm:p-8 lg:p-10"
            >
              <div>
                <h2 id="guide-help-title" className="text-2xl font-bold tracking-[-0.015em] text-title-blue">
                  <FormattedMessage id="guide.help.title" />
                </h2>
                <p className="mt-1.5 text-base text-text-main">
                  <FormattedMessage id="guide.help.desc" />
                </p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <Link
                  href={`${homePath(locale)}#contact`}
                  className="inline-flex items-center justify-center rounded-full border border-blue-sec bg-surface px-5 py-2.5 text-sm font-semibold text-brand transition-colors hover:border-blue-main focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-main"
                >
                  <FormattedMessage id="guide.help.contact" />
                </Link>
                <Link
                  href={helpAppUrl}
                  onClick={() => {
                    trackEvent("guide-help-signup-button", {}, { criticalOnNav: true });
                    trackCtaClick("guide-help", helpAppTarget);
                  }}
                  className="inline-flex items-center justify-center rounded-full bg-blue-main px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-main/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-main"
                >
                  <FormattedMessage id="guide.cta.signup" />
                </Link>
              </div>
            </section>
          </div>
        </div>
      </div>

      <MobileToc
        active={active}
        hidden={helpInView}
        stepTitle={stepTitle}
        resourcesHref={resourcesHref}
      />
    </div>
  );
}

type TocProps = {
  active: string | null;
  stepTitle: (s: GuideStep) => string;
  resourcesHref: string;
  /** Called after a link is followed (the mobile sheet closes itself). */
  onNavigate?: () => void;
};

/**
 * The contents as a progress path: steps already read are ticked in their colour, the
 * current one is filled and set in bold, the ones ahead stay grey, and a counter says how
 * far along the reader is. Then the links to the feature map and to the resources.
 */
function TocContents({ active, stepTitle, resourcesHref, onNavigate }: TocProps) {
  const total = GUIDE_STEPS.length;
  const index = stepIndex(active);
  return (
    <>
      <p className="flex items-baseline justify-between px-1 text-sm font-semibold text-title-blue">
        <FormattedMessage id="guide.toc.title" />
        <span className="tabular-nums font-medium text-gray-main">
          {Math.min(Math.max(index + 1, 0), total)}/{total}
        </span>
      </p>
      <ol className="mt-3 flex flex-col">
        {GUIDE_STEPS.map((s, k) => {
          const on = k === index;
          const done = k < index;
          const tone = SPOT_TONES[s.tone];
          return (
            <li key={s.id} className="relative">
              {/* Rail between markers: coloured once the step above is read. */}
              {k < total - 1 && (
                <span
                  aria-hidden="true"
                  className={`absolute left-[14px] top-[30px] h-[calc(100%-22px)] w-px ${done ? tone.solid : "bg-gray-light"}`}
                />
              )}
              <a
                href={`#${s.id}`}
                onClick={onNavigate}
                aria-current={on ? "location" : undefined}
                className={`relative flex items-start gap-3 rounded-lg px-1 py-2 text-sm leading-snug transition-colors focus-visible:outline-2 focus-visible:outline-blue-main ${
                  on ? "font-semibold text-title-blue" : done ? "text-title-blue hover:text-brand" : "text-sec-blue hover:text-title-blue"
                }`}
              >
                <span
                  aria-hidden="true"
                  className={`mt-px flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-bold tabular-nums transition-colors ${
                    on || done ? `${tone.badge} text-white` : "border border-gray-main bg-surface text-gray-main"
                  }`}
                >
                  {done ? <FaCheck className="text-[10px]" /> : k + 1}
                </span>
                {stepTitle(s)}
              </a>
            </li>
          );
        })}
      </ol>
      <div className="mt-4 flex flex-col gap-1 border-t border-gray-light px-1 pt-4 text-sm">
        <a
          href={`#${FEATURES_ID}`}
          onClick={onNavigate}
          aria-current={active === FEATURES_ID ? "location" : undefined}
          className={`rounded py-1 hover:text-brand focus-visible:outline-2 focus-visible:outline-blue-main ${
            active === FEATURES_ID ? "font-semibold text-brand" : "text-title-blue"
          }`}
        >
          <FormattedMessage id="guide.toc.features" />
        </a>
        <Link
          href={resourcesHref}
          className="rounded py-1 text-title-blue hover:text-brand focus-visible:outline-2 focus-visible:outline-blue-main"
        >
          <FormattedMessage id="guide.toc.resources" />
        </Link>
      </div>
    </>
  );
}

/**
 * Phones and tablets: a pill pinned to the bottom of the screen names the step being read
 * (its number in the step's colour, and n/7) and opens the full contents above itself.
 * Closes on a link, on Escape and on a tap outside; steps aside (and leaves the tab order)
 * once the help block at the end is on screen, so it never covers the footer.
 */
function MobileToc({ active, hidden, stepTitle, resourcesHref }: Omit<TocProps, "onNavigate"> & { hidden: boolean }) {
  const intl = useIntl();
  const [wantOpen, setOpen] = useState(false);
  // At the end of the guide the pill steps aside, and its sheet with it.
  const open = wantOpen && !hidden;
  const rootRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const total = GUIDE_STEPS.length;
  const index = stepIndex(active);
  const step = GUIDE_STEPS[index] as GuideStep | undefined;
  const panelId = "guide-toc-sheet";

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      buttonRef.current?.focus();
    };
    const onPointer = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [open]);

  const label =
    active === FEATURES_ID ? intl.formatMessage({ id: "guide.toc.features" }) : step ? stepTitle(step) : intl.formatMessage({ id: "guide.toc.title" });

  return (
    <div
      inert={hidden}
      data-guide-toc=""
      className={`pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center px-4 pb-[max(1rem,env(safe-area-inset-bottom))] transition duration-300 motion-reduce:transition-none lg:hidden ${
        hidden ? "translate-y-4 opacity-0" : "translate-y-0 opacity-100"
      }`}
    >
      <div ref={rootRef} className="pointer-events-auto relative w-full max-w-md">
        {open && (
          <nav
            id={panelId}
            aria-label={intl.formatMessage({ id: "guide.toc.label" })}
            className="absolute inset-x-0 bottom-full mb-2 max-h-[min(70vh,34rem)] overflow-y-auto overscroll-contain rounded-3xl border border-gray-light bg-surface p-4 shadow-xl"
          >
            <TocContents active={active} stepTitle={stepTitle} resourcesHref={resourcesHref} onNavigate={() => setOpen(false)} />
          </nav>
        )}
        <button
          ref={buttonRef}
          type="button"
          aria-expanded={open}
          // The sheet is only in the DOM while open.
          aria-controls={open ? panelId : undefined}
          onClick={() => setOpen(!open)}
          className="flex w-full items-center gap-3 rounded-full border border-gray-light bg-surface py-2 pl-2 pr-4 text-left shadow-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-main"
        >
          <span
            aria-hidden="true"
            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold tabular-nums ${
              step ? `${SPOT_TONES[step.tone].badge} text-white` : active === FEATURES_ID ? "bg-blue-fill text-white" : "border border-gray-main text-gray-main"
            }`}
          >
            {active === FEATURES_ID ? <FaCheck className="text-[11px]" /> : step ? index + 1 : 1}
          </span>
          <span className="min-w-0 flex-1 truncate text-sm font-semibold text-title-blue">
            <span className="sr-only">{intl.formatMessage({ id: "guide.toc.label" })}{" · "}</span>
            {label}
          </span>
          <span aria-hidden="true" className="shrink-0 text-sm tabular-nums text-gray-main">
            {Math.min(Math.max(index + 1, 0), total)}/{total}
          </span>
          <FaChevronUp
            aria-hidden="true"
            className={`shrink-0 text-xs text-gray-main transition-transform motion-reduce:transition-none ${open ? "rotate-180" : ""}`}
          />
        </button>
      </div>
    </div>
  );
}

/**
 * One step, as a help-centre section: numbered title and lead, the demo, then the
 * numbered how-to, an optional tip and action, and the detailed articles. On wide screens
 * the text runs in one column and the demo sits beside it; below, the demo follows the
 * lead so each step opens on its picture.
 */
function StepSection({
  step,
  n,
  locale,
  links,
  stepTitle,
}: {
  step: GuideStep;
  n: number;
  locale: Locale;
  links: GuideArticleLinks;
  stepTitle: (s: GuideStep) => string;
}) {
  const intl = useIntl();
  const [nearRef, near] = useInView<HTMLElement>("60% 0px 60% 0px");
  const [playRef, playing] = useInView<HTMLDivElement>("0px", 0.6);
  const articles = step.articles.map((ref) => links[ref]).filter(Boolean);
  const headingId = `${step.id}-title`;
  const tone = SPOT_TONES[step.tone];
  const ctaPlacement = `guide-step-${step.id}`;
  const stepCta = useAppLink(ctaPlacement, { target: "web", webBase: step.cta });

  return (
    <section
      ref={nearRef}
      id={step.id}
      aria-labelledby={headingId}
      className={`${SCROLL_MARGIN} grid grid-cols-1 gap-x-12 py-12 sm:py-14 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] xl:grid-rows-[auto_1fr] xl:py-16 2xl:gap-x-20`}
    >
      <div className="xl:col-start-1 xl:row-start-1">
        <h2 id={headingId} className="flex items-start gap-3.5 text-balance text-2xl font-bold leading-tight tracking-[-0.02em] text-title-blue sm:text-[28px]">
          <span
            aria-hidden="true"
            className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold tabular-nums text-white sm:mt-1 ${tone.badge}`}
          >
            {n}
          </span>
          <span>
            <span className="sr-only">
              {intl.formatMessage({ id: "guide.step.of" }, { n, total: GUIDE_STEPS.length })}
              {colon(locale)}
            </span>
            {stepTitle(step)}
          </span>
        </h2>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-text-main sm:text-lg">
          <FormattedMessage id={`guide.step.${step.id}.lead`} />
        </p>
      </div>

      <div
        ref={playRef}
        aria-hidden="true"
        className={`relative mt-8 aspect-[5/4] w-full sm:aspect-[16/10] xl:aspect-[5/4] ${phoneStageClass(step.scene)} overflow-hidden rounded-[28px] xl:sticky xl:top-[calc(var(--promo-h,0px)+8.5rem)] xl:col-start-2 xl:row-span-2 xl:row-start-1 xl:mt-0 xl:self-start ${tone.ground}`}
      >
        <StoryLottie
          name={step.scene}
          locale={locale}
          load={near}
          active={playing}
          label={stepTitle(step)}
          className="absolute inset-0"
        />
      </div>

      <div className="mt-8 max-w-2xl xl:col-start-1 xl:row-start-2">
        <h3 className="sr-only">
          <FormattedMessage id="guide.howto" />
        </h3>
        <ol className="flex flex-col gap-3">
          {Array.from({ length: step.how }, (_, i) => (
            <li key={i} className="flex gap-3 text-base leading-relaxed text-text-main">
              <span aria-hidden="true" className={`w-4 shrink-0 font-semibold tabular-nums ${tone.text}`}>
                {i + 1}
              </span>
              <span>
                <FormattedMessage id={`guide.step.${step.id}.how.${i + 1}`} values={rich} />
              </span>
            </li>
          ))}
        </ol>

        {step.tip && (
          <p className={`mt-7 border-l-2 pl-4 text-[15px] leading-relaxed text-sec-blue ${tone.bar}`}>
            <strong className="font-semibold text-title-blue">
              <FormattedMessage id="guide.tip" />
              {colon(locale)}
            </strong>
            <FormattedMessage id={`guide.step.${step.id}.tip`} values={rich} />
          </p>
        )}

        {step.cta && (
          <a
            href={stepCta.href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              trackEvent(`guide-${step.id}-cta`, {}, { criticalOnNav: true });
              if (appTargetOfUrl(step.cta ?? "")) trackCtaClick(ctaPlacement, "web");
            }}
            className="group mt-6 inline-flex items-center gap-2 rounded-full bg-blue-main px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-main/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-main"
          >
            <FormattedMessage id={`guide.step.${step.id}.cta`} />
            <FaArrowRight aria-hidden="true" className="text-xs transition-transform group-hover:translate-x-0.5 motion-reduce:transition-none" />
          </a>
        )}

        {articles.length > 0 && (
          <>
            <h3 className="sr-only">
              <FormattedMessage id="guide.readMore" />
            </h3>
            <ul className="mt-8 flex flex-col gap-2.5 border-t border-gray-light pt-6">
              {articles.map((a) => (
                <li key={a.href}>
                  <Link
                    href={a.href}
                    className="group inline-flex items-start gap-2.5 rounded py-0.5 text-[15px] leading-snug text-brand-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-main"
                  >
                    <FaRegFileLines aria-hidden="true" className="mt-[3px] shrink-0 text-[13px] text-gray-main group-hover:text-brand" />
                    <span className="group-hover:underline group-hover:underline-offset-4">{a.title}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </section>
  );
}

/** The feature map: four groups of tools, each a line of copy and a link to its article. */
function FeatureMap({ links }: { links: GuideArticleLinks }) {
  return (
    <section id={FEATURES_ID} aria-labelledby={`${FEATURES_ID}-title`} className={`${SCROLL_MARGIN} pt-12 sm:pt-14 xl:pt-16`}>
      <h2
        id={`${FEATURES_ID}-title`}
        className="text-balance text-3xl font-bold leading-tight tracking-[-0.02em] text-title-blue sm:text-4xl"
      >
        <FormattedMessage id="guide.features.title" />
        <span className="text-brand">.</span>
      </h2>
      <p className="mt-3 text-base text-text-main sm:text-lg">
        <FormattedMessage id="guide.features.lead" />
      </p>

      <div className="mt-10 grid gap-x-12 gap-y-10 sm:grid-cols-2 2xl:gap-x-20">
        {GUIDE_FEATURE_GROUPS.map((group) => (
          <div key={group.id}>
            <h3 className="border-b border-gray-light pb-3 text-base font-semibold text-title-blue">
              <FormattedMessage id={`guide.features.group.${group.id}`} />
            </h3>
            <ul className="mt-5 flex flex-col gap-5">
              {group.features.map((f) => {
                const link = f.articles.map((ref) => links[ref]).find(Boolean);
                const title = <FormattedMessage id={`guide.feature.${f.id}.title`} />;
                return (
                  <li key={f.id} className="flex gap-3.5">
                    <SpotIcon name={f.spot} className={`mt-px h-7 w-7 shrink-0 ${SPOT_TONES[group.tone].ink}`} />
                    <div className="min-w-0">
                      {link ? (
                        <Link
                          href={link.href}
                          className="group inline-flex items-center gap-2 rounded text-base font-semibold text-title-blue hover:text-brand focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-main"
                        >
                          {title}
                          <FaArrowRight
                            aria-hidden="true"
                            className="text-[10px] text-gray-sec transition group-hover:translate-x-0.5 group-hover:text-brand motion-reduce:transition-none"
                          />
                        </Link>
                      ) : (
                        <span className="text-base font-semibold text-title-blue">{title}</span>
                      )}
                      <p className="mt-0.5 text-sm leading-relaxed text-text-main">
                        <FormattedMessage id={`guide.feature.${f.id}.desc`} />
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}
